import React, { useState, useRef, useEffect, lazy, Suspense, useCallback } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Icon,
  Flex,
  Spinner,
  Badge,
  keyframes,
  SimpleGrid,
  useBreakpointValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
} from '@chakra-ui/react';
import {
  FaChartArea,
  FaChartLine,
  FaSmile,
  FaGlobe,
  FaTools,
  FaSearchDollar,
  FaBullhorn,
  FaTasks,
  FaBullseye,
  FaStar,
  FaCalculator,
  FaDollarSign,
  FaImage,
  FaTimes,
  FaArrowLeft,
} from 'react-icons/fa';
import { MdTrendingUp } from 'react-icons/md';
import { getDemoData } from '../data/dashboardDemoData';

const TotalReviewsDashboard = lazy(() => import('./dashboards/TotalReviewsDashboard'));
const GrossGrowthDashboard = lazy(() => import('./dashboards/GrossGrowthDashboard'));
const SentimentDashboard = lazy(() => import('./dashboards/SentimentDashboard'));
const MarketTrendsDashboard = lazy(() => import('./dashboards/MarketTrendsDashboard'));
const WebTrafficDashboard = lazy(() => import('./dashboards/WebTrafficDashboard'));
const BusinessInsightsDashboard = lazy(() => import('./dashboards/BusinessInsightsDashboard'));
const CompetitiveIntelDashboard = lazy(() => import('./dashboards/CompetitiveIntelDashboard'));
const KeywordIntelligenceDashboard = lazy(() => import('./dashboards/KeywordIntelligenceDashboard'));
const AdsDashboard = lazy(() => import('./dashboards/AdsDashboard'));
const KeywordsDashboard = lazy(() => import('./dashboards/KeywordsDashboard'));
const FavoriteDashboard = lazy(() => import('./dashboards/FavoriteDashboard'));
const LocalyticsScoreDashboard = lazy(() => import('./dashboards/LocalyticsScoreDashboard'));
const PricingDashboard = lazy(() => import('./dashboards/PricingDashboard'));
const ContentTemplatesDashboard = lazy(() => import('./dashboards/ContentTemplatesDashboard'));
const SpyfuKeywordDashboard = lazy(() => import('./dashboards/SpyfuKeywordDashboard'));
const OrganicPaidTrafficDashboard = lazy(() => import('./dashboards/OrganicPaidTrafficDashboard'));
const CompetitorMapDashboard = lazy(() => import('./dashboards/CompetitorMapDashboard'));
const SetGoalsPage = lazy(() => import('./playbook/SetGoalsPage'));
const PlaybookActionsPage = lazy(() => import('./playbook/PlaybookActionsPage'));

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(0, 102, 255, 0.15); }
  50% { box-shadow: 0 0 30px rgba(0, 102, 255, 0.3); }
`;

const DASHBOARD_QUESTIONS = [
  {
    id: 'total-reviews',
    title: 'Market Authority',
    question: 'Who has the most reviews?',
    description: 'See every business ranked by total review count',
    icon: FaChartArea,
    color: '#2F80ED',
    gradient: 'linear-gradient(135deg, #2F80ED 0%, #56CCF2 100%)',
  },
  {
    id: 'gross-growth',
    title: 'Review Growth',
    question: 'Who\'s growing fastest?',
    description: 'Review growth velocity over the past 12 months',
    icon: FaChartLine,
    color: '#27AE60',
    gradient: 'linear-gradient(135deg, #27AE60 0%, #6FCF97 100%)',
  },
  {
    id: 'sentiment',
    title: 'Sentiment Leaders',
    question: 'Who do customers love?',
    description: 'Positive, neutral & negative sentiment breakdown',
    icon: FaSmile,
    color: '#48BB78',
    gradient: 'linear-gradient(135deg, #48BB78 0%, #38B2AC 100%)',
  },
  {
    id: 'market-trends',
    title: 'Month in Review',
    question: 'What\'s the market momentum?',
    description: 'This month\'s shifts in reviews and ratings',
    icon: MdTrendingUp,
    color: '#0066FF',
    gradient: 'linear-gradient(135deg, #0066FF 0%, #38B2AC 100%)',
  },
  {
    id: 'web-traffic',
    title: 'SEO',
    question: 'Who\'s winning in SEO?',
    description: 'Search visibility and domain strength rankings',
    icon: FaGlobe,
    color: '#9F7AEA',
    gradient: 'linear-gradient(135deg, #9F7AEA 0%, #667EEA 100%)',
  },
  {
    id: 'business-insights',
    title: 'Business Insights',
    question: 'What are each business\'s strengths?',
    description: 'AI-powered SWOT analysis for every competitor',
    icon: FaTools,
    color: '#ED8936',
    gradient: 'linear-gradient(135deg, #ED8936 0%, #F6AD55 100%)',
  },
  {
    id: 'market-intelligence',
    title: 'Market Intelligence',
    question: 'How does the landscape look?',
    description: 'Live competitive intel across all key metrics',
    icon: FaSearchDollar,
    color: '#E53E3E',
    gradient: 'linear-gradient(135deg, #E53E3E 0%, #FC8181 100%)',
  },
  {
    id: 'keyword-intelligence',
    title: 'Traffic Champions',
    question: 'Who gets the most traffic?',
    description: 'Estimated monthly visitors and traffic trends',
    icon: FaGlobe,
    color: '#3182CE',
    gradient: 'linear-gradient(135deg, #3182CE 0%, #63B3ED 100%)',
  },
  {
    id: 'keywords',
    title: 'Keywords',
    question: 'Which keywords matter most?',
    description: 'Keyword rankings across the competitive landscape',
    icon: FaSearchDollar,
    color: '#805AD5',
    gradient: 'linear-gradient(135deg, #805AD5 0%, #D6BCFA 100%)',
  },
  {
    id: 'ads-intelligence',
    title: 'Google Ads',
    question: 'Who\'s spending on Google Ads?',
    description: 'Competitor ad activity, spend, and ad copy',
    icon: FaBullhorn,
    color: '#D53F8C',
    gradient: 'linear-gradient(135deg, #D53F8C 0%, #FBB6CE 100%)',
  },
  {
    id: 'spyfu-keywords',
    title: 'Keyword Leaders',
    question: 'What keywords do competitors target?',
    description: 'Organic and paid keyword strategies revealed',
    icon: FaSearchDollar,
    color: '#6B46C1',
    gradient: 'linear-gradient(135deg, #6B46C1 0%, #9F7AEA 100%)',
  },
  {
    id: 'organic-paid-traffic',
    title: 'Organic vs Paid',
    question: 'Organic vs Paid — who wins?',
    description: 'Compare organic search vs paid ad performance',
    icon: FaChartArea,
    color: '#38A169',
    gradient: 'linear-gradient(135deg, #38A169 0%, #F6E05E 100%)',
  },
  {
    id: 'localytics-score',
    title: 'Score Champions',
    question: 'Who has the best overall score?',
    description: 'Composite ranking across all performance metrics',
    icon: FaCalculator,
    color: '#5A67D8',
    gradient: 'linear-gradient(135deg, #5A67D8 0%, #7F9CF5 100%)',
  },
  {
    id: 'pricing',
    title: 'Pricing Analysis',
    question: 'How does pricing compare?',
    description: 'Price perception from real customer reviews',
    icon: FaDollarSign,
    color: '#DD6B20',
    gradient: 'linear-gradient(135deg, #DD6B20 0%, #F6AD55 100%)',
  },
  {
    id: 'favorites',
    title: 'My Favorite',
    question: 'Deep-dive into any business?',
    description: 'Full analysis of a selected competitor',
    icon: FaStar,
    color: '#D69E2E',
    gradient: 'linear-gradient(135deg, #D69E2E 0%, #FEFCBF 100%)',
  },
  {
    id: 'content-templates',
    title: 'Content Strategy',
    question: 'What content should you create?',
    description: 'AI-generated marketing templates for your niche',
    icon: FaImage,
    color: '#00B5D8',
    gradient: 'linear-gradient(135deg, #00B5D8 0%, #76E4F7 100%)',
  },
  {
    id: 'playbook-goals',
    title: 'Set Goals',
    question: 'What goals should you set?',
    description: 'Data-driven targets based on your market position',
    icon: FaBullseye,
    color: '#DD6B20',
    gradient: 'linear-gradient(135deg, #DD6B20 0%, #FEFCBF 100%)',
  },
  {
    id: 'playbook-actions',
    title: 'Action Items',
    question: 'What actions move the needle?',
    description: 'Prioritized action items based on competitive gaps',
    icon: FaTasks,
    color: '#38A169',
    gradient: 'linear-gradient(135deg, #38A169 0%, #9AE6B4 100%)',
  },
];

const QuestionBubble = React.memo(({ item, isActive, onClick, index }) => {
  return (
    <Box
      as="button"
      onClick={onClick}
      role="group"
      position="relative"
      bg={isActive ? 'rgba(0, 102, 255, 0.12)' : 'rgba(255, 255, 255, 0.04)'}
      border="1px solid"
      borderColor={isActive ? item.color : 'whiteAlpha.200'}
      borderRadius="2xl"
      px={{ base: 4, md: 5 }}
      py={{ base: 3, md: 4 }}
      cursor="pointer"
      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      _hover={{
        bg: isActive ? 'rgba(0, 102, 255, 0.18)' : 'rgba(255, 255, 255, 0.08)',
        borderColor: item.color,
        transform: 'translateY(-3px)',
        boxShadow: `0 8px 25px ${item.color}33`,
      }}
      animation={isActive ? `${glowPulse} 2s ease-in-out infinite` : undefined}
      textAlign="left"
      w="100%"
      opacity={0}
      sx={{
        animation: isActive
          ? `${glowPulse} 2s ease-in-out infinite`
          : undefined,
        '@keyframes fadeSlideIn': {
          from: { opacity: 0, transform: 'translateY(15px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        animationName: isActive ? undefined : 'fadeSlideIn',
        animationDuration: '0.5s',
        animationDelay: `${index * 0.04}s`,
        animationFillMode: 'forwards',
        animationTimingFunction: 'ease-out',
      }}
    >
      <VStack spacing={2} align="flex-start" w="100%">
        {/* Dashboard title header — same style as the actual product */}
        <HStack spacing={2} align="center">
          <Flex
            w="28px"
            h="28px"
            borderRadius="lg"
            bg={item.gradient}
            justify="center"
            align="center"
            flexShrink={0}
            transition="all 0.3s"
          >
            <Icon
              as={item.icon}
              boxSize={3}
              color="white"
            />
          </Flex>
          <Text mb={0}
            fontSize={{ base: 'sm', md: 'md' }}
            fontWeight="800"
            bgGradient={`linear(to-r, ${item.color}, ${item.color}99)`}
            bgClip="text"
            lineHeight="1.2"
            transition="all 0.3s"
          >
            {item.title}
          </Text>
          {isActive && (
            <Badge
              bg={item.gradient}
              color="white"
              fontSize="9px"
              px={2}
              py={0.5}
              borderRadius="full"
              flexShrink={0}
            >
              LIVE
            </Badge>
          )}
        </HStack>

        {/* Question and description */}
        <VStack spacing={0.5} align="flex-start" w="100%">
          <Text
            fontSize={{ base: 'xs', md: 'sm' }}
            fontWeight="600"
            color={isActive ? 'white' : 'gray.300'}
            lineHeight="1.3"
          >
            {item.question}
          </Text>
          <Text
            fontSize="xs"
            color={isActive ? 'gray.300' : 'gray.500'}
            lineHeight="1.3"
          >
            {item.description}
          </Text>
        </VStack>
      </VStack>
    </Box>
  );
});
QuestionBubble.displayName = 'QuestionBubble';

const DashboardLoadingFallback = () => (
  <Flex
    justify="center"
    align="center"
    minH="400px"
    w="100%"
    bg="rgba(15, 23, 42, 0.5)"
    borderRadius="2xl"
    border="1px solid"
    borderColor="whiteAlpha.100"
  >
    <VStack spacing={4}>
      <Spinner size="xl" color="blue.400" thickness="3px" />
      <Text color="gray.400" fontSize="sm">
        Loading dashboard...
      </Text>
    </VStack>
  </Flex>
);

const DashboardRenderer = React.memo(({ dashboardId }) => {
  const demoData = getDemoData(dashboardId);
  const { reviewsData, yearData, dateRange } = demoData;

  const commonProps = {
    demoMode: true,
    demoData,
    reviewsData,
    dateRange,
  };

  const renderDashboard = () => {
    switch (dashboardId) {
      case 'total-reviews':
        return <TotalReviewsDashboard {...commonProps} />;
      case 'gross-growth':
        return <GrossGrowthDashboard {...commonProps} />;
      case 'sentiment':
        return <SentimentDashboard {...commonProps} />;
      case 'market-trends':
        return <MarketTrendsDashboard {...commonProps} />;
      case 'web-traffic':
        return <WebTrafficDashboard {...commonProps} />;
      case 'business-insights':
        return <BusinessInsightsDashboard {...commonProps} />;
      case 'market-intelligence':
        return <CompetitiveIntelDashboard {...commonProps} />;
      case 'keyword-intelligence':
        return <KeywordIntelligenceDashboard {...commonProps} />;
      case 'keywords':
        return <KeywordsDashboard {...commonProps} />;
      case 'ads-intelligence':
        return <AdsDashboard {...commonProps} />;
      case 'spyfu-keywords':
        return <SpyfuKeywordDashboard {...commonProps} />;
      case 'organic-paid-traffic':
        return <OrganicPaidTrafficDashboard {...commonProps} />;
      case 'localytics-score':
        return <LocalyticsScoreDashboard {...commonProps} />;
      case 'pricing':
        return <PricingDashboard {...commonProps} />;
      case 'favorites':
        return <FavoriteDashboard {...commonProps} />;
      case 'content-templates':
        return <ContentTemplatesDashboard {...commonProps} sectionLabel="Junk Removal" />;
      case 'playbook-goals':
        return <SetGoalsPage {...commonProps} />;
      case 'playbook-actions':
        return <PlaybookActionsPage {...commonProps} sectionLabel="Junk Removal" />;
      case 'competitor-map':
        return <CompetitorMapDashboard {...commonProps} yearData={yearData} />;
      default:
        return null;
    }
  };

  return (
    <Suspense fallback={<DashboardLoadingFallback />}>
      {renderDashboard()}
    </Suspense>
  );
});
DashboardRenderer.displayName = 'DashboardRenderer';

function LandingDashboardShowcase({
  // Optional copy overrides — let parent landing pages tailor the headline
  // (e.g. the market-report page wants "answered in one private dashboard"
  // instead of the default "answered instantly").
  heading = 'Every question about your market,',
  headingHighlight = 'answered instantly',
  subheading = 'Click any question below to explore a live, interactive dashboard built from real competitive data. No signup required.',
} = {}) {
  const [activeDashboard, setActiveDashboard] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const columns = useBreakpointValue({ base: 1, sm: 2, lg: 3 });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.05 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);


  const handleBubbleClick = useCallback((id) => {
    if (activeDashboard === id) {
      setActiveDashboard(null);
      return;
    }
    setActiveDashboard(id);
  }, [activeDashboard]);

  const handleClose = useCallback(() => {
    setActiveDashboard(null);
  }, []);

  const activeItem = DASHBOARD_QUESTIONS.find((q) => q.id === activeDashboard);

  return (
    <>
    <Box
      ref={containerRef}
      py={{ base: 16, md: 24 }}
      bg="linear-gradient(180deg, #0a0f1a 0%, #0d1321 50%, #0a0f1a 100%)"
      position="relative"
      overflow="hidden"
    >
      <Box
        position="absolute"
        top="10%"
        left="-10%"
        w="400px"
        h="400px"
        bg="radial-gradient(circle, rgba(0, 102, 255, 0.06) 0%, transparent 70%)"
        pointerEvents="none"
      />
      <Box
        position="absolute"
        bottom="10%"
        right="-10%"
        w="500px"
        h="500px"
        bg="radial-gradient(circle, rgba(159, 122, 234, 0.04) 0%, transparent 70%)"
        pointerEvents="none"
      />

      <Container maxW="90vw" position="relative" zIndex={1}>
        <VStack spacing={{ base: 10, md: 14 }}>
          <VStack spacing={4} textAlign="center" maxW="800px">
            <Badge
              bg="whiteAlpha.100"
              color="blue.300"
              px={4}
              py={1.5}
              borderRadius="full"
              fontSize="xs"
              fontWeight="bold"
              letterSpacing="wider"
              textTransform="uppercase"
              border="1px solid"
              borderColor="whiteAlpha.200"
            >
              19 Interactive Dashboards
            </Badge>
            <Heading
              fontSize={{ base: '2xl', md: '4xl', lg: '5xl' }}
              fontWeight="800"
              lineHeight="1.1"
              color="white"
              opacity={isVisible ? 1 : 0}
              transform={isVisible ? 'translateY(0)' : 'translateY(20px)'}
              transition="all 0.8s ease-out"
            >
              {heading}{' '}
              <Text
                as="span"
                bgGradient="linear(to-r, #0066FF, #56CCF2)"
                bgClip="text"
              >
                {headingHighlight}
              </Text>
            </Heading>
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              color="gray.400"
              maxW="600px"
              opacity={isVisible ? 1 : 0}
              transform={isVisible ? 'translateY(0)' : 'translateY(15px)'}
              transition="all 0.8s ease-out 0.2s"
            >
              {subheading}
            </Text>
          </VStack>

          <SimpleGrid
            columns={columns}
            spacing={{ base: 3, md: 4 }}
            w="100%"
          >
            {DASHBOARD_QUESTIONS.map((item, index) => (
              <QuestionBubble
                key={item.id}
                item={item}
                isActive={activeDashboard === item.id}
                onClick={() => handleBubbleClick(item.id)}
                index={index}
              />
            ))}
          </SimpleGrid>

        </VStack>
      </Container>
    </Box>

    <Modal
      isOpen={!!(activeDashboard && activeItem)}
      onClose={handleClose}
      isCentered
      motionPreset="slideInBottom"
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
      <ModalContent
        mx={{ base: 2, md: 6 }}
        my={{ base: 2, md: 6 }}
        borderRadius="2xl"
        overflow="hidden"
        maxW={{ base: 'calc(100vw - 16px)', md: 'calc(100vw - 48px)', lg: '1400px' }}
        h={{ base: 'calc(100vh - 16px)', md: 'calc(100vh - 48px)' }}
        maxH={{ base: 'calc(100vh - 16px)', md: 'calc(100vh - 48px)' }}
        bg="#f7f8fa"
        display="flex"
        flexDirection="column"
      >
        {/* Header bar */}
        <Box
          flexShrink={0}
          bg="white"
          borderBottom="1px solid"
          borderColor="gray.200"
          boxShadow="0 2px 8px rgba(0,0,0,0.06)"
          px={{ base: 4, md: 6 }}
          py={3}
          borderTopRadius="2xl"
        >
          <Flex align="center" justify="space-between" w="100%">
            <HStack spacing={3}>
              <Box
                as="button"
                onClick={handleClose}
                display="flex"
                alignItems="center"
                gap={2}
                px={3}
                py={2}
                borderRadius="lg"
                bg="gray.100"
                color="gray.700"
                fontWeight="600"
                fontSize="sm"
                _hover={{ bg: 'gray.200' }}
                transition="all 0.2s"
              >
                <Icon as={FaArrowLeft} boxSize={3} />
                <Text as="span">Back</Text>
              </Box>
              {activeItem && (
                <>
                  <Flex
                    w="36px"
                    h="36px"
                    borderRadius="lg"
                    bg={activeItem.gradient}
                    justify="center"
                    align="center"
                    flexShrink={0}
                  >
                    <Icon as={activeItem.icon} color="white" boxSize={4} />
                  </Flex>
                  <VStack spacing={0} align="flex-start" gap={0}>
                    <Text
                      fontSize={{ base: 'sm', md: 'lg' }}
                      fontWeight="800"
                      bgGradient={`linear(to-r, ${activeItem.color}, ${activeItem.color}99)`}
                      bgClip="text"
                      lineHeight="1"
                      mb="0.5"
                    >
                      {activeItem.title}
                    </Text>
                    <Text color="gray.500" fontSize="xs" lineHeight="1">
                      {activeItem.question} · Junk Removal, Nashville TN · Demo
                    </Text>
                  </VStack>
                </>
              )}
            </HStack>
            <Box
              as="button"
              onClick={handleClose}
              p={2}
              borderRadius="lg"
              bg="gray.100"
              _hover={{ bg: 'gray.200' }}
              transition="all 0.2s"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={FaTimes} color="gray.500" boxSize={4} />
            </Box>
          </Flex>
        </Box>

        {/* Dashboard content */}
        <ModalBody
          p={0}
          flex={1}
          overflowY="auto"
          overflowX="hidden"
          bg="#f7f8fa"
          sx={{ WebkitOverflowScrolling: 'touch' }}
        >
          <Box
            bg="white"
            borderRadius={{ base: 0, md: 'lg' }}
            boxShadow="sm"
            p={{ base: 3, md: 6 }}
            m={{ base: 0, md: 4 }}
            minH="100%"
            border={{ base: 'none', md: '1px solid' }}
            borderColor={{ base: 'transparent', md: 'gray.200' }}
            overflow="hidden"
          >
            <Box
              sx={{
                transform: 'scale(0.75)',
                transformOrigin: 'top left',
                width: '133.33%',
              }}
            >
              <DashboardRenderer dashboardId={activeDashboard} />
            </Box>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
    </>
  );
}

export default LandingDashboardShowcase;
